<template>
  <v-layout class="grey lighten-4" column>
    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <div class="title primary--text">{{ section }}</div>
      <v-spacer />
      <v-tooltip left>
        <template
          v-slot:activator="{ on }">
          <v-btn
            class="mr-3"
            v-on="on"
            :href="exportRoute"
            target="_blank"
            dark
            fab
            x-small
            depressed
            v-if="purchasesPermissions['export-purchases-report']"
            :disabled="!product || dates.length < 2"
            color="primary">
            <v-icon color="white">mdi-application-export</v-icon>
          </v-btn>
        </template>
        <span>Exportar compras a CSV</span>
      </v-tooltip>
      <template>
        <v-btn
          rounded
          class="mr-3"
          small
          depressed
          :disabled="!product || dates.length < 2"
          color="secondary"
          v-if="purchasesPermissions['export-purchases-report']"
          @click="downloadInvoices">
          Descargar facturas
          <!-- v-if="movementsPermissions['download-invoices-xml-zip']" -->
        </v-btn>
      </template>
    </div>

    <v-flex xs12>
      <v-data-table
        :loading-text="table.title"
        :server-items-length="table.total"
        :loading="table.loading"
        :headers="headers"
        :items="table.data"
        :options="table.options"
        :footer-props="{
          'items-per-page-options': [10, 20, 30, 40, 50]
        }"
        no-data-text="Selecciona un producto y el rango de fecha"
        sort-by="name"
        @update:options="updateTable"
        class="elevation-1 fixed-header">
        <template v-slot:top>
          <div class="d-flex align-center pa-3">
            <div class="mr-3">
              Filtros:
            </div>
            <div class="mr-3">
              <v-autocomplete
                prepend-inner-icon="mdi-magnify"
                rounded
                outlined
                hide-details
                flat
                dense
                hide-no-data
                hide-selected
                clearable
                item-text="name"
                item-value="uuid"
                label="Buscar producto"
                v-model="product"
                :items="products"
                :loading="loadingProducts"
                @input.native="searchProducts"
              />
            </div>
            <div class="mr-3">
              <v-menu
                ref="datePicker"
                v-model="datePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
                :disabled="!product"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="range"
                    label="Rango de fechas"
                    prepend-icon="mdi-calendar"
                    readonly
                    hide-details
                    dense
                    v-on="on"
                    :disabled="!product"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dates"
                  :range="true"
                  first-day-of-week="1"
                  no-title
                  locale="es-mx"
                  :max="untilToday"
                >
                  <div>
                    <div class="px-2 pb-3">
                      <v-btn-toggle
                        v-model="statusChanges"
                        mandatory
                        rounded
                        color="primary"
                        active-class="primary white--text"
                      >
                        <v-btn value="confirmed" class="ml-0" small color="white">
                          Confirmado en esta fecha
                        </v-btn>
                        <v-btn value="received" class="ml-0" small color="white">
                          Recibido en esta fecha
                        </v-btn>
                      </v-btn-toggle>
                    </div>
                    <div class="d-flex">
                      <v-btn text color="primary" @click="resetDate">Borrar</v-btn>
                      <v-spacer/>
                      <v-btn text color="grey" @click="datePicker = false">Cancel</v-btn>
                      <v-btn :disabled="dates.length < 2" color="primary" @click="filterData">OK</v-btn>
                    </div>
                  </div>
                </v-date-picker>
              </v-menu>
            </div>
          </div>
        </template>
        <template v-slot:item.quantity="{ item }">
          <div class="text-right">
            <div>{{ item.quantity }}</div>
            <div class="grey--text">{{ item.unit }}</div>
          </div>
        </template>
        <template v-slot:item.date="{ item }">{{ item.date | toDate }}</template>
        <template v-slot:item.amount="{ item }"><div class="text-right">{{ item.amount | toFixNumber(2) | numberWithComma | prefix('$') }}</div></template>
        <template v-slot:item.amountWithTax="{ item }"><div class="text-right">{{ item.amountWithTax | toFixNumber(2) | numberWithComma | prefix('$') }}</div></template>
        <template v-slot:item.totalAmount="{ item }"><div class="text-right">{{ item.totalAmount | toFixNumber(2) | numberWithComma | prefix('$') }}</div></template>
        <template v-slot:item.totalWithTax="{ item }"><div class="text-right">{{ item.totalWithTax | toFixNumber(2) | numberWithComma | prefix('$') }}</div></template>
        <template v-slot:item.IVA="{ item }"><div class="text-right">{{ item.IVA | toFixNumber(2) | numberWithComma | prefix('$') }}</div></template>
        <template v-slot:item.IEPS="{ item }"><div class="text-right">{{ item.IEPS | toFixNumber(2) | numberWithComma | prefix('$') }}</div></template>
      </v-data-table>
    </v-flex>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-layout>
</template>

<script>
import moment from 'moment'
import { debounce } from 'lodash'

export default {
  data () {
    return {
      overlay: false,
      datePicker: false,
      dates: [],
      section: 'Compras generadas',
      table: {
        title: 'Cargando compras generadas',
        loading: false,
        data: [],
        total: 1,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 50,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: [''],
          sortDesc: []
        }
      },
      product: '',
      products: [],
      loadingProducts: false,
      statusChanges: 'confirmed'
    }
  },
  computed: {
    purchasesPermissions () {
      return this.$store.state.login.permissions['my-management'].components['purchases-generated-list'].actions
    },
    exportRoute () {
      return `${process.env.VUE_APP_API_URL}/v1/inputs/purchases-by-product/export?access_token=${this.$store.state.login.token}&restaurant=${this.$store.state.currentRestaurant.data._id}&product=${this.product}&createdAt>=${this.dates[0] || ''}&createdAt<=${this.dates[1] || ''}&statusChanges=${this.statusChanges}`
    },
    range () {
      return this.dates.length < 2 ? '' : `${this.dates[0]} / ${this.dates[1]}`
    },
    untilToday () {
      return moment().format('YYYY-MM-DD')
    },
    headers () {
      const headers = [
        { text: 'Producto', value: 'name', sortable: false },
        { text: 'Proveedor', value: 'provider', sortable: false },
        { text: 'Cantidad recibida', value: 'quantity', sortable: false },
        { text: 'Precio unitario (sin impuestos)', value: 'amount', sortable: false },
        { text: 'Precio unitario (con impuestos)', value: 'amountWithTax', sortable: false },
        { text: 'Total (sin impuestos)', value: 'totalAmount', sortable: false },
        { text: 'Total (con impuestos)', value: 'totalWithTax', sortable: false },
        { text: 'IVA', value: 'IVA', sortable: false },
        { text: 'IEPS', value: 'IEPS', sortable: false }
      ]
      if (this.statusChanges === 'confirmed') {
        headers.unshift({ text: 'Fecha de confirmación', value: 'date', sortable: false })
      } else {
        headers.unshift({ text: 'Fecha de recepción', value: 'date', sortable: false })
      }

      return headers
    }
  },
  methods: {
    async resetDate () {
      this.dates = []
      this.table.data = []
      this.datePicker = false
    },
    async filterData () {
      let dates = {}
      if (this.dates.length) {
        this.dates.sort((a, b) => {
          if (a < b) return -1
          if (a > b) return 1
          return 0
        })
        dates['createdAt>'] = moment(this.dates[0]).startOf('day').toISOString()
        dates['createdAt<'] = moment(this.dates[1]).endOf('day').toISOString()
      }

      const params = {
        ...dates,
        product: this.product,
        restaurant: this.$store.state.currentRestaurant.data._id,
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1))
      }

      if (this.statusChanges) {
        params['statusChanges'] = this.statusChanges
      }

      await this.load(params)
      this.datePicker = false
    },
    updateTable: async function (data) {
      if (this.product && this.dates.length) {
        this.table.options = data
        await this.load({
          limit: data.itemsPerPage,
          skip: (data.itemsPerPage * (data.page - 1))
        })
      }
    },
    async load (params = null) {
      params = params || {
        restaurant: this.$store.state.currentRestaurant.data._id,
        product: this.product,
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1))
      }
      this.table.loading = true
      try {
        const response = await this.$api.get(`/v1/inputs/purchases-by-product`, params)
        this.table.data = response.data
        this.table.total = response.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    async downloadInvoices () {
      this.overlay = true
      const startDate = moment(this.dates[0])
      const endDate = moment(this.dates[1])
      if (endDate.diff(startDate, 'months', true) > 2) {
        this.$toasted.show(`El rango de fecha ${startDate.format('YYYY-MM-DD')} | ${startDate.format('YYYY-MM-DD')} es mayor a 2 meses, intenta con un rango máximo de 2 meses para continuar.`, { duration: 6000 })
      } else if (this.dates.length === 0) {
        this.$toasted.show(`Seleccione un rango de fechas.`, { duration: 6000 })
      } else {
        try {
          await this.$api.get(`/v1/inputs/purchases-by-product/exportInvoices?zip=zip&access_token=${this.$store.state.login.token}&restaurant=${this.$store.state.currentRestaurant.data._id}&product=${this.product}&statusChanges=${this.statusChanges}&createdAt>=${startDate.startOf('day').toISOString() || ''}&createdAt<=${endDate.endOf('day').toISOString() || ''}`)
          this.$toasted.show('Se enviará a su correo el link para descargar las facturas emitadas', { duration: 3000, type: 'success' })
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
      }
      this.overlay = false
    },
    searchProducts: debounce(async function (event) {
      const params = { name: `/${event.target.value}/i`, skip: 0, listAll: true, limit: 20 }
      this.loadingProducts = true

      try {
        const products = await this.$api.get('/v1/products', params)
        this.products = products.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }

      this.loadingProducts = false
    }, 300)
  }
}
</script>
